import { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary to catch and display errors in the app
 * Especially useful for debugging native Capacitor builds
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            minHeight: '100vh',
            backgroundColor: '#1a1a2e',
            color: '#fff',
            padding: '20px',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1 style={{ color: '#f87171', marginBottom: '16px' }}>
            Something went wrong
          </h1>
          <div
            style={{
              backgroundColor: '#2a2a3e',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              overflow: 'auto',
            }}
          >
            <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {this.state.error?.toString()}
            </pre>
          </div>
          {this.state.errorInfo && (
            <div
              style={{
                backgroundColor: '#2a2a3e',
                borderRadius: '8px',
                padding: '16px',
                overflow: 'auto',
                maxHeight: '300px',
              }}
            >
              <p style={{ color: '#94a3b8', marginBottom: '8px', fontSize: '14px' }}>
                Component Stack:
              </p>
              <pre style={{ fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#94a3b8' }}>
                {this.state.errorInfo.componentStack}
              </pre>
            </div>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              backgroundColor: '#0ea5e9',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer',
            }}
          >
            Reload App
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
